import { BaseError } from "@src/components/DefuseSDK/errors/base"
import type { WalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import { toError } from "@src/utils/errors"

export type WalletConnectionFailureType =
  | "rejected"
  | "popup_blocked"
  | "timeout"
  | "network_error"
  | "technical_error"
  | "unknown"

/** Max characters for error messages sent to analytics. */
export const MAX_ERROR_MESSAGE_LENGTH = 200

/** User-initiated failures that should NOT trigger Sentry alerts. */
const BENIGN_WALLET_FAILURES: ReadonlySet<WalletConnectionFailureType> =
  new Set(["rejected", "popup_blocked"])

export class WalletConnectionFailure extends BaseError {
  readonly failureType: WalletConnectionFailureType
  readonly errorCode: WalletErrorCode | null
  readonly originalMessage: string

  constructor(error: unknown, errorCode: WalletErrorCode | null) {
    const failureType = classify(error, errorCode)
    super(`Wallet connection failed: ${failureType}`, {
      name: "WalletConnectionFailure",
      cause: error,
    })
    this.failureType = failureType
    this.errorCode = errorCode
    this.originalMessage = toError(error).message
  }

  get isActionable(): boolean {
    return !BENIGN_WALLET_FAILURES.has(this.failureType)
  }

  /** Original error message, truncated for analytics. */
  get truncatedMessage(): string {
    return this.originalMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH)
  }
}

function classify(
  error: unknown,
  errorCode: WalletErrorCode | null
): WalletConnectionFailureType {
  if (errorCode === "ERR_WALLET_POPUP_BLOCKED") return "popup_blocked"
  if (errorCode === "ERR_WALLET_CANCEL_ACTION") return "rejected"
  if (errorCode === "ERR_WALLET_WEBAUTHN_TIMEOUT_OR_CANCELLED") return "timeout"

  const err = toError(error)
  const msg = err.message.toLowerCase()

  if (err.name === "TimeoutError") return "timeout"
  if (msg.includes("timeout") || msg.includes("timed out")) return "timeout"

  if (
    msg.includes("fetch") ||
    msg.includes("network") ||
    msg.includes("rpc") ||
    msg.includes("econnrefused")
  ) {
    return "network_error"
  }

  if (msg.length > 0) return "technical_error"

  return "unknown"
}
